import { ExpandMore, ConfirmationNumber, ExpandLess } from '@mui/icons-material';
import {
    Box,
    Typography,
    Alert,
    Collapse,
    Button,
    Grid,
    LinearProgress,
    Stack,
    IconButton,
} from '@mui/material';
import { useState } from 'react';

import TicketCard from './TicketCard';

import type { TicketInterface } from 'types';

interface TicketsAlertProps {
    tickets: TicketInterface[] | undefined;
    compact?: boolean;
    maxVisibleTickets?: number;
    resolvingIds: Set<number>;

    handleToggleResolve: (ticket: TicketInterface, isResolved: boolean) => Promise<void>;
}

const TicketsAlert = ({
    tickets = [],
    compact = false,
    maxVisibleTickets = 3,
    resolvingIds,
    handleToggleResolve,
}: TicketsAlertProps) => {
    const [expanded, setExpanded] = useState(!false);

    if (!tickets || tickets.length === 0) return null;

    const openTickets = tickets.filter(
        (t) => t.status.toLowerCase().includes('pending') || t.status.toLowerCase().includes('open')
    );
    const resolvedTickets = tickets.filter(
        (t) =>
            t.status.toLowerCase().includes('resolved') ||
            t.status.toLowerCase().includes('approved')
    );

    const visibleTickets = expanded ? tickets : tickets.slice(0, maxVisibleTickets);
    const hiddenCount = Math.max(0, tickets.length - maxVisibleTickets);

    if (compact) {
        return (
            <Alert severity={openTickets.length > 0 ? 'warning' : 'info'} sx={{ mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {tickets.length} Feedback Item
                            {tickets.length !== 1 ? 's' : ''} on Your Documents
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {openTickets.length} open • {resolvedTickets.length} resolved
                        </Typography>
                    </Box>
                </Box>
            </Alert>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Alert
                severity={openTickets.length > 0 ? 'warning' : 'info'}
                icon={<ConfirmationNumber />}
                sx={{
                    mb: 1,
                    '& .MuiAlert-action': { p: 0 },
                    '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                    <IconButton disableRipple onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                }
            >
                <Stack
                    sx={{
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {tickets.length} Feedback Item
                            {tickets.length !== 1 ? 's' : ''} from Agent Review
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Feedback on your uploaded documents
                        </Typography>
                    </Box>

                    {/* Progress Bars */}
                    <Box sx={{ width: { xs: '100%', sm: 400 } }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '0.75rem',
                            }}
                        >
                            <Typography variant="caption">{openTickets.length} Open</Typography>
                            <Typography variant="caption">
                                {resolvedTickets.length} Resolved
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={
                                (resolvedTickets.length /
                                    (openTickets.length + resolvedTickets.length || 1)) *
                                100
                            }
                            sx={{ height: 6, borderRadius: 1 }}
                        />
                    </Box>
                </Stack>
            </Alert>

            {/* Visible Tickets */}
            <Collapse in={expanded}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {visibleTickets.map((ticket) => (
                        <Grid size={{ xs: 12, sm: 6, xl: 4 }} key={ticket.id}>
                            <TicketCard
                                ticket={ticket}
                                resolvingIds={resolvingIds}
                                onToggleResolve={handleToggleResolve}
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* Show more button */}
                {hiddenCount > 0 && !expanded && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(true)}
                            startIcon={<ExpandMore />}
                        >
                            Show {hiddenCount} more
                        </Button>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export default TicketsAlert;
